/**
 * V2 PaAthenaInfoCheck
 * Athena patient verification form
 */

import { memo, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import produce from 'immer';
import { useStaticLocales } from '../../../../admin/hooks/locales/use-static-locale';
import { usePatientLocale } from '../../../hooks/use-patient-locale';
import Footer from '../../../../app/Footer';
import PaSurveyHeader from '../../../../app/PaSurveyHeader';
import SurveyContainer from './SurveyContainer';
import il1 from '../../../../patient/survey/assets/DoctorPatient.svg';
import { usePatientBasePath } from '../../../../app/route-utils';

/**
 * Page Content Component
 */
function PaAthenaInfoCheckContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const patientBasePath = usePatientBasePath();
  const { localeCode } = usePatientLocale();
  const { sltStr } = useStaticLocales({ localeCode });

  // Get all data from Container
  const {
    setReportState,
    setSurveyState,
    athena,
    navigation,
    resetAllStates,
  } = SurveyContainer.useContainer();

  const { checkPatientInfo } = athena;
  const { params } = navigation;
  const { orgId, shortId } = params;

  // Local state
  const [lastName, setLastName] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Get URL parameters
  const searchParams = new URLSearchParams(location.search);
  const urlAid = searchParams.get('aid');
  const eid = searchParams.get('eid');

  // Format date as MM/DD/YYYY
  const formatDate = (value: string): string => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length <= 2) return cleaned;
    if (cleaned.length <= 4) return `${cleaned.slice(0, 2)}/${cleaned.slice(2)}`;
    return `${cleaned.slice(0, 2)}/${cleaned.slice(2, 4)}/${cleaned.slice(4, 8)}`;
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatDate(e.target.value);
    setBirthDate(formatted);
  };

  // Handle confirm button click
  const handleConfirm = useCallback(async () => {
    if (!lastName.trim() || !birthDate.trim()) {
      setError(sltStr({ key: 'msg-please-fill-all-fields' }));
      return;
    }

    if (!urlAid) {
      setError(sltStr({ key: 'msg-appointment-id-required' }));
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Check if AID has changed
      const previousAid = localStorage.getItem('currentAid');
      if (urlAid !== previousAid) {
        // Reset all states for new patient
        resetAllStates();
      }

      // Store current AID
      localStorage.setItem('currentAid', urlAid);

      // Check patient info via Athena
      const patientDetails = await checkPatientInfo(urlAid, lastName, birthDate);

      if (patientDetails) {
        // Update report state with patient info
        setReportState((st) =>
          produce(st, (dr) => {
            if (!dr.systemValueMap) dr.systemValueMap = {};

            if (patientDetails.dob) {
              dr.systemValueMap.dateOfBirth = patientDetails.dob;
            }
            if (patientDetails.lastName) {
              dr.systemValueMap.lastName = patientDetails.lastName;
            }
            if (patientDetails.firstName) {
              dr.systemValueMap.firstName = patientDetails.firstName;
            }
            if (patientDetails.middleName) {
              dr.systemValueMap.middleName = patientDetails.middleName;
            }
            if (patientDetails.gender) {
              dr.systemValueMap.gender = patientDetails.gender;
            }
            if (patientDetails.phoneNumber) {
              dr.systemValueMap.phoneNumber = patientDetails.phoneNumber;
            }
            if (patientDetails.email) {
              dr.systemValueMap.email = patientDetails.email;
            }
            if (patientDetails.address) {
              dr.systemValueMap.address = patientDetails.address;
            }
            if (patientDetails.city) {
              dr.systemValueMap.city = patientDetails.city;
            }
            if (patientDetails.state) {
              dr.systemValueMap.state = patientDetails.state;
            }
            if (patientDetails.zipCode) {
              dr.systemValueMap.zipCode = patientDetails.zipCode;
            }
          })
        );

        // Update survey state with patient details and encounter info
        setSurveyState((st) =>
          produce(st, (dr) => {
            dr.patientDetails = patientDetails;
            dr.encounterId = eid || '';
            dr.encounterCheckStatus = 'checking';
            dr.encounterCheckReason = undefined;
          })
        );

        // Navigate to survey start page
        navigate(`${patientBasePath}/${orgId}/${shortId}?aid=${urlAid}&eid=${eid || ''}`);
      } else {
        setError(sltStr({ key: 'msg-patient-not-found' }));
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : sltStr({ key: 'msg-unknown-error' });
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [
    lastName,
    birthDate,
    urlAid,
    eid,
    orgId,
    shortId,
    sltStr,
    resetAllStates,
    checkPatientInfo,
    setReportState,
    setSurveyState,
    navigate,
  ]);

  return (
    <div className='flex-col flex-1 w-full gap-5 px-5 pt-2'>
      <div className='flex-col flex-1 w-full gap-6'>
        <PaSurveyHeader />

        <div className='text-xl flex-col gap-3'>
          <div />
          <div className='h-1' />
          <h4 className='font-bold sm'>{sltStr({ key: 'title-verify-patient' })}</h4>
          <p className='md text-light'>{sltStr({ key: 'desc-verify-patient' })}</p>
        </div>

        {/* Patient Last Name Section */}
        <div className='form-control'>
          <label className='form-label'>
            {sltStr({ key: 'label-patient-last-name' })}
          </label>
          <div className='hint-text text-xs opacity-70'>
            {sltStr({ key: 'hint-legal-name-english' })}
          </div>
          <input
            type='text'
            className='input input-bordered w-full'
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            placeholder={sltStr({ key: 'placeholder-enter-last-name' })}
            disabled={isLoading}
          />
        </div>

        {/* Patient Birth Date Section */}
        <div className='form-control'>
          <label className='form-label'>
            {sltStr({ key: 'label-patient-birth-date' })}
          </label>
          <div className='hint-text text-xs opacity-70'>
            {sltStr({ key: 'hint-month-date-year' })}
          </div>
          <input
            type='text'
            className='input input-bordered w-full'
            value={birthDate}
            onChange={handleDateChange}
            placeholder={sltStr({ key: 'placeholder-mm-dd-yyyy' })}
            maxLength={10}
            disabled={isLoading}
          />
        </div>

        {/* Error Message */}
        {error && (
          <p className='text-error text-sm -mt-3'>{error}</p>
        )}

        {/* Confirm Button */}
        <div className='flex-col gap-2'>
          <button
            className='btn btn-lg btn-primary w-full'
            onClick={handleConfirm}
            disabled={isLoading || !lastName.trim() || !birthDate.trim()}
          >
            {isLoading ? sltStr({ key: 'btn-checking' }) : sltStr({ key: 'btn-confirm' })}
          </button>
        </div>
      </div>

      <Footer />
    </div>
  );
}

/**
 * Main Page Component
 */
function PaAthenaInfoCheck() {
  return (
    <SurveyContainer.Provider>
      <PaAthenaInfoCheckContent />
    </SurveyContainer.Provider>
  );
}

export default memo(PaAthenaInfoCheck);
